import React, { useCallback, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Alert } from "mesh-component-library";
import FirstNameLastNameZipLookup from "../../components/lookupForms/FirstNameLastNameZipLookup";
import FirstNameLastNameCustomerTypeLookup from "../../components/lookupForms/FirstNameLastNameCustomerTypeLookup";
import PhoneNumberLookup from "../../components/lookupForms/PhoneNumberLookup";
import PolicyMemberLookup from "../../components/lookupForms/PolicyMemberLookup";
import MotorNumberLookup from "../../components/lookupForms/MotorNumberLookup";
import {
  benefitsLookupStart,
  commonBenefitsStart,
  noCommonBenefits,
  requestPartnerDetails,
  updatePpuFlag,
  lastSuccessfulSessionType,
  updateIsPrimaryLookup,
  refreshReducersToInitial,
  updateLoggedInFlag,
  updateContactNumber,
} from "../../action";
import Spinner from "../../components/spinner";
import {
  getPartnerDetails,
  getCommonBenefits,
  getExpirationMileageByIndex,
} from "../../selector";
import { generatePath } from "../../utilities/RedirectUtils";
import SearchByLicensePlatePage from "../../components/lookupForms/SearchByLicensePlatePage";
import { ConfirmationModal } from "../InformationConfirmationModal/ConfirmationModal";
import VinOnlyLookupForm from "../../components/lookupForms/VinOnlyLookup";
import TransactionNumberLookup from "../../components/lookupForms/TransactionNumberLookup";
import { datadogRum } from "@datadog/browser-rum";
import { useLocale } from "../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import StartOverAlertMessage from "../../components/start-over-alert/StartOverAlertMessage";
import TriageStepsFixedAlert from "../../components/triage-steps-fixed-alert/TriageStepsFixedAlert";
import lastNameStateZipLookup from "../../components/lookupForms/LastNameStateZipLookup";
import CONSTANTS, {
  NOT_COVERED_REASONS,
  WRD,
  ACTIVE_MEMBERSHIP_STATUSES,
  MEMBER_NOT_FOUND_ERROR_MESSAGES,
  SERVICE_TRANSITION_EXCLUDED_LOOKUP_TYPES,
  ERU,
} from "../../app-consts/appConstants";
import PolicyNumberStateCodeLookup from "../../components/lookupForms/PolicyNumberStateCodeLookup";
import OdometerLookup from "../../components/lookupForms/OdometerLookup";
import RentalAgreementLookup from "../../components/lookupForms/RentalAgreementLookup";
import { getEnhancedAuthBenefits } from "../../features/EnhancedAuth/action";
import { getOmniStreamlinedFlag } from "../../features/FeatureFlags";
import {normalizeMessageData} from "../../features/EnhancedAuth/helper";
import { OemDeflectionScreen } from "../../features/WarrantyDeflection/components";
import { getWarrantyDeflectionOemNumber } from "../../features/WarrantyDeflection/selectors";

const LookupComponents = {
  firstNameLastNameZipLookup: FirstNameLastNameZipLookup,
  phoneNumberLookup: PhoneNumberLookup,
  firstNameLastNameCustomerTypeLookup: FirstNameLastNameCustomerTypeLookup,
  memberlookup: FirstNameLastNameZipLookup,
  memberNumberLookup: PolicyMemberLookup,
  policyNumberLookup: PolicyMemberLookup,
  policyNumberLastNameLookup: PolicyMemberLookup,
  memberNumberLastNameLookup: PolicyMemberLookup,
  licensePlateLookup: SearchByLicensePlatePage,
  vinOnlyLookup: VinOnlyLookupForm,
  motorNumberLookup: MotorNumberLookup,
  lastNameStateZipLookup: lastNameStateZipLookup,
  memberNumberVinLookup: lastNameStateZipLookup,
  policyNumberStateCodeLookup: PolicyNumberStateCodeLookup,
  odometerLookUp: OdometerLookup,
  oemDeflection: OemDeflectionScreen,
  licensePlateOrVinLookup: SearchByLicensePlatePage,
  rentalAgreementLookup: RentalAgreementLookup,
  phoneNumberLastNameLookup: PhoneNumberLookup,
  phoneNumberLastNameTransactionNumberLookup: PhoneNumberLookup,
  transactionNumberLookup: TransactionNumberLookup,
};

const CommonLookup = ({ history }) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalOptions, setModalOptions] = useState({});
  const [hasUsedAlternate, setHasUsedAlternate] = useState(false);
  const showSpinner = useSelector((state) => !!state.commonBenefits.commonBenefitLoading);
  const partnerDetails = useSelector(getPartnerDetails);
  const commonBenefits = useSelector(getCommonBenefits);
  const expirationMileage = useSelector((state) =>
    getExpirationMileageByIndex(state, 0)
  );
  const warrantyDeflectionOemNumber = useSelector(getWarrantyDeflectionOemNumber);
  const dispatch = useDispatch();
  const primaryLookup =
    partnerDetails.experience?.lookupMethods?.components.primary;
  const alternateLookup =
    partnerDetails.experience?.lookupMethods?.components.alternate;
  const primaryFormValidations =
    partnerDetails.experience?.lookupMethodRules?.primary || {};
  const alternateFormValidations =
    partnerDetails.experience?.lookupMethodRules?.alternate || {};
  const primarySessionType =
    partnerDetails.experience?.lookupMethods?.sessionType?.primary;
  const alternateSessionType =
    partnerDetails.experience?.lookupMethods?.sessionType?.alternate;
  const enhancedAuthPartner = partnerDetails.enhancedAuthPartner;
  const isEmkayPartner = partnerDetails.partnerCode === ERU;
  const [currentLookup, setCurrentLookup] = useState(primaryLookup);
  const [useAlternateLookup, setUseAlternateLookup] = useState(false);
  const getTranslatedText = useLocale();
  const [isPartnerChanged, setPartnerChanged] = useState(false);
  const [primaryLookupAttempted, setPrimaryLookupAttempted] = useState(0);
  const [alternateLookupAttempted, setAlternateLookupAttempted] = useState(0);
  const omniStreamlined = useSelector(getOmniStreamlinedFlag);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(()=>{
    if (sessionStorage.getItem("skipRehydrate")) {
      dispatch(refreshReducersToInitial());
      sessionStorage.removeItem('skipRehydrate')
    }
  },[])

  useEffect(() => {
    setCurrentLookup(primaryLookup);
  }, [primaryLookup]);

  // If current lookup needs to be set on second render
  if (currentLookup === undefined && primaryLookup !== undefined) {
    setCurrentLookup(primaryLookup);
  }

  useEffect(() => {
    if (commonBenefits.errorMessage === CONSTANTS.SERVICE_REQUEST_DENIED) {
      history.push(generatePath("ServiceTransition"));
    }
    datadogRum.addAction("User lands on CommonLookup");
    const currentPartnerCode = window.location.pathname.split("/")[2];
    const routeChanged =
      partnerDetails.route !== window.location.pathname.split("/")[1];
    if (
      partnerDetails.experience &&
      currentPartnerCode !== undefined &&
      currentPartnerCode !== partnerDetails.partnerCode &&
      currentPartnerCode !== partnerDetails.displayCode
    ) {
      datadogRum.addAction(
        `User changed partner code from ${partnerDetails.displayCode} to ${currentPartnerCode}`
      );
      dispatch(refreshReducersToInitial());
      localStorage.clear();
      dispatch(requestPartnerDetails());
      setPartnerChanged(true);
    } else if (partnerDetails.experience && routeChanged) {
      dispatch(requestPartnerDetails());
    }
  }, []);

  useEffect(() => {
    if (!partnerDetails.experience) {
      dispatch(requestPartnerDetails());
    }
  }, [partnerDetails.experience]);

  useEffect(() => {
    if (!currentLookup || currentLookup === primaryLookup) {
      dispatch(updateIsPrimaryLookup(true));
    } else {
      dispatch(updateIsPrimaryLookup(false));
    }
  }, [currentLookup]);

  useEffect(() => {
    if (
      partnerDetails.partnerCode !== undefined &&
      partnerDetails.partnerCode === window.location.pathname.split("/")[2] &&
      partnerDetails.partnerCode !== partnerDetails.displayCode
    ) {
      history.push(generatePath("memberlookup", partnerDetails.displayCode));
    }
  }, [partnerDetails, window.location.pathname.split("/")[2] !== undefined]);

  useEffect(() => {
    const emkayMembershipStatus = isEmkayPartner
      ? commonBenefits.status?.toUpperCase()
      : null;
    const isEmkayMembershipActive =
      emkayMembershipStatus === CONSTANTS.ACTIVE;

    if (
      commonBenefits.commonBenefitLoading === false &&
      !partnerDetails.experience?.ppuEnabled &&
      !partnerDetails.experience?.benefitsEnabled &&
      partnerDetails.experience?.serviceSelectionEnabled
    ) {
      history.push(generatePath("home"));
    }

    if (
      !!omniStreamlined &&
      NOT_COVERED_REASONS.find(
        (pair) => pair.id === commonBenefits.status?.toUpperCase()
      )
    ) {
      history.push(generatePath("home"));
    }

    if (
      partnerDetails.experience?.serviceBased &&
      ((commonBenefits.lookupType === CONSTANTS.AUTO_PHONE_LOOKUP && commonBenefits.status === CONSTANTS.INACTIVE) ||
        (!SERVICE_TRANSITION_EXCLUDED_LOOKUP_TYPES.includes(commonBenefits.lookupType) &&
          MEMBER_NOT_FOUND_ERROR_MESSAGES.includes(commonBenefits.errorMessage)))
    ) {
      history.push(generatePath("ServiceTransition"));
    }
    if (
      emkayMembershipStatus &&
      !isEmkayMembershipActive &&
      commonBenefits.commonBenefitLoading === false
    ) {
      history.push(generatePath("ServiceTransition"));
      return;
    }
    // If benefits lookup API has finished
    if (useAlternateLookup && commonBenefits.commonBenefitLoading === false) {
      localStorage.removeItem("rescueCancel");
      localStorage.removeItem("isPpu");
      localStorage.removeItem("feedbackConfirmation");

      dispatch(updateIsPrimaryLookup(false));
      // If there weren't any benefits found
      // If an error comes back from looking for benefits or
      // When benefits are not found and reducer initializes experience to an empty object
      if (
        (commonBenefits.experience?.claimsExhausted &&
          !partnerDetails.experience?.ppuEnabled) ||
        commonBenefits.errorMessage === CONSTANTS.SERVICE_REQUEST_DENIED
      ) {
        history.push(generatePath("ServiceTransition"));
      } else if (
        commonBenefits.error ||
        (commonBenefits.noBenefits &&
          !partnerDetails.experience?.claimsExhausted)
      ) {
        const redirectTo = () => {
          const location = partnerDetails.experience?.ppuEnabled
            ? "home"
            : "ServiceTransition";
          history.push(generatePath(location));
        };
        if (commonBenefits.lookupType === CONSTANTS.TRANSACTION_NUMBER_LOOKUP) {
          // Transaction number lookup is a terminal lookup do not fall back to another lookup form
          redirectTo();
        } else if (!!primaryLookupAttempted && !!alternateLookupAttempted) {
          redirectTo();
        } else if (!!primaryLookupAttempted) {
          setCurrentLookup(alternateLookup);
        } else if (!!alternateLookupAttempted) {
          setCurrentLookup(primaryLookup);
        } else {
          setCurrentLookup(alternateLookup);
          if (hasUsedAlternate || currentLookup === alternateLookup) {
            redirectTo();
          } else {
            setHasUsedAlternate(true);
          }
        }
      } else if (
        commonBenefits.benefits ||
        partnerDetails.experience?.claimsExhausted
      ) {
        const isActiveWheelsMembership =
          partnerDetails.partnerCode === WRD &&
          ACTIVE_MEMBERSHIP_STATUSES.includes(commonBenefits.status?.toUpperCase());
        if (
          !!expirationMileage ||
          !!warrantyDeflectionOemNumber ||
          isActiveWheelsMembership ||
          isEmkayMembershipActive
        ) {
          setCurrentLookup("odometerLookUp");
        } else {
          history.push(generatePath("home"));
        }
      }
    }
  }, [commonBenefits.commonBenefitLoading]);

  useEffect(() => {
    //if enhance auth not enabled then return
    if (!partnerDetails.enhancedAuthPartner) return;

    const sendReadyMessage = () => {
      const message = { type: "APP_LOADED", message: "ready" };
      window.postMessage(
        JSON.stringify(message),
        window.location.origin
      );
      console.debug("Sent 'ready' message to the native app.");
      if (window.AndroidHandler?.handleMessage) {
        window.AndroidHandler.handleMessage(JSON.stringify(message));
        console.debug("Sent 'ready' message to the Android Handler.");
      }
      if (window.webkit?.messageHandlers?.webviewHandler?.postMessage) {
        window.webkit.messageHandlers.webviewHandler.postMessage(JSON.stringify(message));
        console.debug("Sent 'ready' message to the webkit Handler.");
      }
    };

    const handleNativeMessage = (event) => {
      try {
        const messageData = normalizeMessageData(JSON.parse(event.data));
        // Append customer phone # to serviceRequestPayload for V15
        if (messageData.phoneNumber) {
          dispatch(updateContactNumber(messageData.phoneNumber));
        }
        dispatch(getEnhancedAuthBenefits({...messageData, partnerCode: partnerDetails.partnerCode}));
        setUseAlternateLookup(true);
      } catch (error) {
        datadogRum.addError("Error handling message:", error);
      }
    };

    window.addEventListener("message", handleNativeMessage);
    sendReadyMessage();

    return () => {
      window.removeEventListener("message", handleNativeMessage);
      if (window.AndroidHandler?.handleMessage) {
        delete window.AndroidHandler.handleMessage;
      }
    };

  }, [dispatch, partnerDetails]);

  const dispatchCommonBenefitsLookup = useCallback(
    (data) => {
      data.sessionType = primarySessionType;
      if (currentLookup === alternateLookup) {
        data.sessionType = alternateSessionType;
      }
      setUseAlternateLookup(true);
      dispatch(lastSuccessfulSessionType(data.sessionType));
      if (sessionStorage.getItem("invalid_session")) {
        sessionStorage.setItem("invalid_session", "false");
      }
      if (!!omniStreamlined) {
        dispatch(benefitsLookupStart(data, history));
      } else {
        dispatch(commonBenefitsStart(data));
      }
    },
    [currentLookup, omniStreamlined]
  );

  const showModal = useCallback((options) => {
    setModalOptions({
      header: getTranslatedText(TRANSLATION_CONSTANTS.IS_INFORMATION_CORRECT),
      ...options,
    });
    setShowConfirmationModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOptions({});
    setShowConfirmationModal(false);
  }, []);

  const continueAsGuest = () => {
    localStorage.removeItem("rescueCancel");
    localStorage.removeItem("sessionType");
    localStorage.removeItem("benefitSession");
    localStorage.removeItem("memberId");
    localStorage.removeItem("lastName");
    localStorage.removeItem("commonBenefits");
    localStorage.removeItem("zip");
    localStorage.removeItem("ppuFuelPrice");
    localStorage.removeItem("firstName");
    if (sessionStorage.getItem("invalid_session")) {
      sessionStorage.setItem("invalid_session", "false");
    }
    if (!!omniStreamlined) {
      const user = {};
      user.phoneLookup = true;
      user.lookupType = "phoneNumber";
      user.guestFlow = true;
      dispatchCommonBenefitsLookup(user);
    } else {
      dispatch(updatePpuFlag("Y"));
      dispatch(
        noCommonBenefits(
          getTranslatedText(TRANSLATION_CONSTANTS.MEMBER_NOT_FOUND)
        )
      );
    }
    dispatch(updateLoggedInFlag("N"));
    datadogRum.addAction("Continued As Guest");
    dispatch(updateIsPrimaryLookup(false));
    history.push(generatePath("home"));
  };

  const { experience } = partnerDetails;
  const hideContinueAsGuest = experience?.hideContinueAsGuest;
  const ppuEnabled = experience?.ppuEnabled;
  const milesBased = experience?.milesBased;
  let currentFormValidations = hasUsedAlternate
    ? alternateFormValidations
    : primaryFormValidations;
  const LookupComponent =
    currentLookup !== undefined
      ? React.createElement(LookupComponents[currentLookup], {
          partnerDetails,
          dispatchCommonBenefitsLookup,
          showModal,
          milesBased,
          closeModal,
          history,
          setUseAlternateLookup,
          setHasUsedAlternate,
          setCurrentLookup,
          currentFormValidations,
          currentLookup,
          enhancedAuthPartner,
          setPrimaryLookupAttempted,
          setAlternateLookupAttempted,
          primaryLookupAttempted,
          alternateLookupAttempted,
        })
      : null;

  return (
    <div className="l-container-memberLookup" id="common-lookup">
      <ConfirmationModal isActive={showConfirmationModal} {...modalOptions} />
      {localStorage.getItem("rescueCancel") === "YES" &&
      localStorage.getItem("isPpu") === "true" ? (
        <div style={{ margin: "1rem" }}>
          <Alert variant="information">
            {getTranslatedText(
              TRANSLATION_CONSTANTS.TAKE_UP_5_DAYS_CREDIT_REFUND
            )}
          </Alert>
        </div>
      ) : null}
      {localStorage.getItem("rescueCancel") === "YES" &&
      localStorage.getItem("isPpu") !== "true" ? (
        <div style={{ margin: "1rem" }}>
          <Alert variant="success">
            {getTranslatedText(
              TRANSLATION_CONSTANTS.CANCEL_REQUEST_SUCCESFULLY
            )}
          </Alert>
        </div>
      ) : null}
      {localStorage.getItem("rescueCancel") !== "YES" &&
      sessionStorage.getItem("invalid_session") === "true"
        ? (() => {
            if (
              !!currentLookup &&
              !!primaryLookup &&
              currentLookup !== primaryLookup
            ) {
              sessionStorage.setItem("invalid_session", "false");
              return;
            }
            return (
              <div style={{ margin: "1rem" }}>
                <Alert variant="information">
                  {getTranslatedText(TRANSLATION_CONSTANTS.INVALID_SESSION)}
                </Alert>
              </div>
            );
          })()
        : null}
      {!isPartnerChanged &&
      localStorage.getItem("feedbackConfirmation") === "YES" ? (
        <div style={{ margin: "1rem" }}>
          <Alert variant="success">
            {getTranslatedText(TRANSLATION_CONSTANTS.APPRECIATE_FEEDBACK)}
          </Alert>
        </div>
      ) : null}
      {!isPartnerChanged && (
        <StartOverAlertMessage
          enhancedAuthPartner={enhancedAuthPartner}
          benefitsLoading={commonBenefits.commonBenefitLoading}
          isAlternateLookup={useAlternateLookup}
          primaryLookup={primaryLookup}
        />
      )}
      <TriageStepsFixedAlert
        benefitsLoading={commonBenefits.commonBenefitLoading}
        isAlternateLookup={useAlternateLookup}
      />
      {showSpinner && <Spinner />}
      {currentLookup && LookupComponent}
      {ppuEnabled && !hideContinueAsGuest && (
        <div>
          <div className="or-text u-align-center u-vr-2-top">
            - {getTranslatedText(TRANSLATION_CONSTANTS.OR)} -
          </div>
          <button
            id="amc-continue-as-guest"
            className="btn btn--tertiary u-vr-3-top"
            onClick={continueAsGuest}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE_AS_GUEST)}
          </button>
          <p className="pay-per-use u-vr-2-top">
            {getTranslatedText(TRANSLATION_CONSTANTS.USE_PPU_OPTION_TO_PAY)}
          </p>
        </div>
      )}
      <div className="c-divider u-vr-5-top"></div>
    </div>
  );
};

export default CommonLookup;
